/**
 * @module app.core
 */

import { autoinject, bindingMode } from 'aurelia-framework';
import { ComponentBase } from 'app/shared/lib/essette-site';
import { IAssessmentsConfig, ITasksConfig } from 'app/core/models/essette-interfaces';
import { bindable } from 'aurelia-typed-observable-plugin';
import { AssessmentService } from 'app/core/services/assessment-service';
import { IQueryLoadOptions } from 'app/shared/lib/api/api-types';
import { IAssessmentAttachmentOptions } from 'app/core/components/assessments/assessment-attachment';
import { ExtendLocalState } from 'app/shared/models/local-state-extensions';
import { ICollapse } from 'app/shared/attributes/ess-collapse-attributes';
import * as Core from 'app/core/models/essette-api';

type AttachmentWithLocalState = ExtendLocalState<Core.AssessmentAttachment, { isLoaded?: boolean; }>;

@autoinject()
export class AssessmentSummary extends ComponentBase {

    ////****************** Fields and Properties ************************************////
    @bindable.stringNull({ defaultBindingMode: bindingMode.toView, changeHandler: 'reload' })
    public targetId: string | null = null;
    @bindable.objectNull({ defaultBindingMode: bindingMode.toView })
    public config: IAssessmentsConfig | null = null;
    @bindable.objectNull({ defaultBindingMode: bindingMode.toView })
    public taskConfig: ITasksConfig | null = null;

    protected ignoreExpandCollapse: boolean = false;
    protected allExpanded: boolean = true;
    protected allCollapsed: boolean = true;
    @bindable.boolean()
    protected showHistoryOption: boolean = true;

    protected assessmentOptions: IAssessmentAttachmentOptions = {
        showActions: false,
        showBack: false,
        showHeader: false,
        showToc: false,
        showMember: false,
        showMemberSummaryLink: false,
        showTarget: false,
        showTargetSummaryLink: false,
        forceRequiredQuestions: false,
        canReopen: true,
        stickyActions: false,
        isTaskDisabled: false
    }

    protected attachments: ReadonlyArray<AttachmentWithLocalState> = [];


    ////****************** Element References ***************************************////

    protected $collapse!: ICollapse;
    protected $listElement!: HTMLElement;

    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(
        private readonly service: AssessmentService,
        ...rest: any[]) {

        super(...rest);
        this.logger.source = this;
    }

    protected bind(bindingContext: Object, overrideContext: Object): void {
        if(!this.targetId) { this.logger.warn('Target ID was not bound'); }
        if(!this.config) { this.logger.warn('Assessment config was not bound'); }

        if(!!this.config) {
            this.service.configure(this.config);
        }

        if(!!this.targetId && !!this.config) {
            this.reload(this.targetId);
        }
    }


    ////****************** Events ***************************************************////


    ////****************** Functions ************************************************////
    public async reload(targetId: string): Promise<void> {
        if(!targetId) { return; }

        this.loadTargetAttachments(targetId);
    }

    protected updateCollapsed(): void {
        if(!this.ignoreExpandCollapse) {
            const counts = this.$collapse.getCounts();
            console.log(counts);
            this.allCollapsed = counts.closed === counts.total;
            this.allExpanded = counts.open === counts.total;
        }
    }

    protected collapseAll(event: Event): void {
        try {
            this.ignoreExpandCollapse = true;
            this.$collapse.closeAll();
            this.allCollapsed = true;
        }
        finally {
            this.ignoreExpandCollapse = false;
        }

        // remove focus from button
        if(event.target instanceof HTMLElement) { event.target.blur(); }
    }

    protected expandAll(event: Event): void {
        // ensure all attachments get loaded
        for(const attachment of this.attachments) {
            if(!attachment._state) {
                attachment._state = {};
            }
            attachment._state.isLoaded = true;
        }

        try {
            this.ignoreExpandCollapse = true;
            this.$collapse.openAll();
            this.allExpanded = true;
        }
        finally {
            this.ignoreExpandCollapse = false;
        }

        // remove focus from button
        if(event.target instanceof HTMLElement) { event.target.blur(); }
    }


    ////****************** Service Calls ********************************************////
    private async loadTargetAttachments(targetId: string): Promise<void> {
        const options: Partial<IQueryLoadOptions> = {
            all: true
        };

        const includes: string[] = [
            'assessment.externalSystem',
        ];
        const fields: string[] = [
            'description',
            'assessment.externalSystem.code',
        ];

        try {
            this.state.beginLoad();
            const query = this.service.createAttachmentsByTargetQuery(targetId, options, includes, null, fields);
            query.filterOr((f) => {
                f.filterIsNull('task.id');
                f.filterBoolean('task.isActive', true);
            });
            const response = await this.service.get<Core.AssessmentAttachment>(query);
            response.results.forEach((a: AttachmentWithLocalState) => {
                a._state = { isLoaded: false };
            });
            this.attachments = response.results;
            // if there are no attachments, they're all expanded
            this.allExpanded = this.attachments.length === 0;
        }
        catch(error) {
            throw error;
        }
        finally {
            this.state.endLoad();
        }
    }

}
